import React, { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./company_info.css";
import {
  Building2,
  Palette,
  Phone,
  Mail,
  Image as ImageIcon,
  Shield,
  MapPin,
  Save,
  Info,
  UploadCloud,
  Loader2,
  CheckCircle2,
} from "lucide-react";

const LOGO_BUCKET = "company-logos"; // <-- change if your bucket name differs

const CompanyInfo = () => {
  const { companyId } = useCompany();
  const [companyInfo, setCompanyInfo] = useState({
    company_name: "",
    legal_name: "",
    rc_number: "",
    address: "",
    TIN: "",
    tin: "",
    email: "",
    phone_number: "",
    logo_url: "",
    brand_colors: "",
    bank_name: "",
    account_name: "",
    account_number: "",
    payment_instructions: "",
  });
  const [loading, setLoading] = useState(true);
  const [isNew, setIsNew] = useState(true);

  // Upload UI state
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // ── Fetch by company_id (unchanged behavior)
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data: ci, error: ciErr } = await supabase
        .from("company_info")
        .select("*")
        .eq("company_id", companyId)
        .maybeSingle();

      if (ciErr && ciErr.code !== "PGRST116") {
        console.error("Error fetching company_info:", ciErr);
      } else if (ci) {
        setCompanyInfo(ci);
        setIsNew(false);
      }
      setLoading(false);
    })();
  }, [companyId]);

  const handleChange = (field, value) =>
    setCompanyInfo((c) => ({ ...c, [field]: value }));

  // ── Upsert by company_id (unchanged behavior)
  const saveCompanyInfo = async () => {
    setLoading(true);
    if (!companyId) {
      alert("No company selected.");
      setLoading(false);
      return;
    }

    const ciPayload = {
      company_id: companyId,
      ...companyInfo,
    };

    const { data: existing, error: fetchErr } = await supabase
      .from("company_info")
      .select("company_id")
      .eq("company_id", companyId)
      .single();

    if (fetchErr && fetchErr.code !== "PGRST116") {
      console.error("Error checking existing company_info:", fetchErr);
      setLoading(false);
      return;
    }

    let result;
    if (existing) {
      result = await supabase
        .from("company_info")
        .update(ciPayload)
        .eq("company_id", companyId);
    } else {
      result = await supabase.from("company_info").insert([ciPayload]);
    }

    if (result.error) {
      console.error("Error saving company_info:", result.error);
      alert("Could not save company details.");
      setLoading(false);
      return;
    }
    setIsNew(false);

    const { error: compErr } = await supabase
      .from("companies")
      .update({ company_name: companyInfo.company_name })
      .eq("id", companyId);
    if (compErr) {
      console.error("Error upserting companies:", compErr);
      alert("Could not save company record.");
      setLoading(false);
      return;
    }

    alert("Company information saved successfully!");
    setLoading(false);
  };

  // ── File upload helpers
  const uploadLogoFile = async (file) => {
    try {
      setUploading(true);
      setUploadMessage("");

      if (!file) throw new Error("No file selected.");
      if (!companyId) throw new Error("No company selected.");

      // Optional: basic type guard
      const validTypes = [
        "image/png",
        "image/jpeg",
        "image/webp",
        "image/svg+xml",
      ];
      if (!validTypes.includes(file.type)) {
        throw new Error("Please upload a PNG, JPG, SVG, or WEBP image.");
      }

      // Create a unique path: companyId/timestamp.ext
      const ext = file.name.split(".").pop();
      const path = `${companyId}/${Date.now()}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from(LOGO_BUCKET)
        .upload(path, file, {
          cacheControl: "3600",
          upsert: true, // overwrite if same name
        });

      if (uploadErr) throw uploadErr;

      // Get public URL
      const { data: pub } = supabase.storage
        .from(LOGO_BUCKET)
        .getPublicUrl(path);

      if (!pub?.publicUrl) throw new Error("Could not resolve public URL.");
      // Update form model
      setCompanyInfo((c) => ({ ...c, logo_url: pub.publicUrl }));
      setUploadMessage("Logo uploaded successfully.");
    } catch (err) {
      console.error(err);
      setUploadMessage(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      setTimeout(() => setUploadMessage(""), 2500);
    }
  };

  // Input & DnD handlers
  const onFilePick = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadLogoFile(file);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadLogoFile(file);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);

  const brandColor = useMemo(
    () => (companyInfo.brand_colors || "").trim(),
    [companyInfo.brand_colors]
  );

  if (loading) {
    return <p>Loading company info…</p>;
  }

  return (
    <div className="company-shell">
      {/* Header Card */}
      <header className="panel company-head">
        <div className="head-left">
          <div className="logo-frame">
            {companyInfo.logo_url ? (
              <img
                src={companyInfo.logo_url}
                alt="Company logo"
                className="company-logo"
              />
            ) : (
              <div className="logo-placeholder" title="No logo">
                <ImageIcon size={20} />
              </div>
            )}
          </div>
          <div className="title">
            <h2>
              <Building2 size={18} /> Company Information
            </h2>
            <p className="muted">
              Manage and update your organization’s details below
            </p>
          </div>
        </div>

        {/* Live Brand Preview */}
        <div className="brand-preview" aria-label="Brand Color Preview">
          <div
            className="swatch"
            style={{
              background:
                brandColor?.startsWith("#") || brandColor?.startsWith("rgb")
                  ? brandColor
                  : `linear-gradient(90deg, var(--brand), var(--brand-2))`,
            }}
            title={brandColor || "Default theme"}
          />
          <span className="muted-small">{brandColor || "Default"}</span>
        </div>
      </header>

      {/* Form */}
      <section className="panel company-form">
        {/* Row: Logo uploader */}
        <div className="grid-1">
          <div
            className={`dropzone ${dragOver ? "drag" : ""}`}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") fileInputRef.current?.click();
            }}
            aria-label="Upload company logo"
          >
            <UploadCloud size={18} />
            <div className="dz-text">
              <strong>Upload Logo</strong>
              <span>Drag & drop your image here, or click to browse</span>
              <small>PNG, JPG, SVG, WEBP • Max ~5MB</small>
            </div>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <Loader2 className="spin" size={14} /> Uploading…
                </>
              ) : (
                "Choose File"
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={onFilePick}
              hidden
            />
          </div>
          {uploadMessage && (
            <div
              className={`upload-msg ${
                uploadMessage.includes("success") ? "ok" : "warn"
              }`}
            >
              {uploadMessage.includes("success") ? (
                <CheckCircle2 size={14} />
              ) : (
                <Info size={14} />
              )}
              <span>{uploadMessage}</span>
            </div>
          )}
        </div>

        {/* Row 1 */}
        <div className="grid-2">
          <label className="stack">
            Company Name
            <div className="input-icon">
              <Building2 size={14} />
              <input
                type="text"
                value={companyInfo.company_name}
                onChange={(e) => handleChange("company_name", e.target.value)}
                placeholder="e.g., KostPadi Ltd."
              />
            </div>
          </label>

          <label className="stack">
            TIN
            <div className="input-icon">
              <Shield size={14} />
              <input
                type="text"
                value={companyInfo.TIN}
                onChange={(e) => handleChange("TIN", e.target.value)}
                placeholder="Tax Identification Number"
              />
            </div>
          </label>
        </div>

        {/* Row 2 */}
        <div className="grid-2">
          <label className="stack">
            Address
            <div className="input-icon">
              <MapPin size={14} />
              <input
                type="text"
                value={companyInfo.address}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder="Company address"
              />
            </div>
          </label>

          <label className="stack">
            Phone Number
            <div className="input-icon">
              <Phone size={14} />
              <input
                type="tel"
                value={companyInfo.phone_number}
                onChange={(e) => handleChange("phone_number", e.target.value)}
                placeholder="+234 800 000 0000"
              />
            </div>
          </label>
        </div>

        {/* Row 3 */}
        <div className="grid-2">
          <label className="stack">
            Email
            <div className="input-icon">
              <Mail size={14} />
              <input
                type="email"
                value={companyInfo.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="hello@company.com"
              />
            </div>
          </label>

          <label className="stack">
            Logo URL
            <div className="input-icon">
              <ImageIcon size={14} />
              <input
                type="text"
                value={companyInfo.logo_url}
                onChange={(e) => handleChange("logo_url", e.target.value)}
                placeholder="https://…/logo.png"
              />
            </div>
          </label>
        </div>

        {/* Row 4 */}
        <div className="grid-2">
          <label className="stack">
            Bank Name
            <div className="input-icon">
              <Building2 size={14} />
              <input
                type="text"
                value={companyInfo.bank_name}
                onChange={(e) => handleChange("bank_name", e.target.value)}
                placeholder="Guaranty Trust Bank"
              />
            </div>
          </label>

          <label className="stack">
            Account Name
            <div className="input-icon">
              <Building2 size={14} />
              <input
                type="text"
                value={companyInfo.account_name}
                onChange={(e) => handleChange("account_name", e.target.value)}
                placeholder="ABC Companies LTD"
              />
            </div>
          </label>
        </div>

        {/* Row 5 */}
        <div className="grid-2">
          <label className="stack">
            Account Number
            <div className="input-icon">
              <Phone size={14} />
              <input
                type="text"
                value={companyInfo.account_number}
                onChange={(e) => handleChange("account_number", e.target.value)}
                placeholder="0312442567"
              />
            </div>
          </label>

          <label className="stack">
            Payment Instructions
            <div className="input-icon">
              <Mail size={14} />
              <input
                type="text"
                value={companyInfo.payment_instructions}
                onChange={(e) =>
                  handleChange("payment_instructions", e.target.value)
                }
                placeholder="At least 50% Upfront Payment is Required to commence work"
              />
            </div>
          </label>
        </div>

        {/* Row 6 */}
        <div className="grid-1">
          <label className="stack">
            Brand Color
            <div className="input-icon">
              <Palette size={14} />
              <input
                type="text"
                value={companyInfo.brand_colors}
                onChange={(e) => handleChange("brand_colors", e.target.value)}
                placeholder="#6c5ce7 or rgb(0,210,255)"
              />
            </div>
            <small className="hint">
              <Info size={12} />
              Supports HEX (e.g. <code>#6c5ce7</code>) or <code>rgb()</code> /
              <code>rgba()</code>. This color is used across documents
              (invoices, receipts) as your brand accent.
            </small>
          </label>
        </div>

        <div className="actions">
          <button
            type="button"
            className="btn-primary"
            onClick={saveCompanyInfo}
            disabled={loading}
          >
            <Save size={16} />
            {loading ? "Saving…" : isNew ? "Save Company" : "Update Company"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default CompanyInfo;
